import { Renderer, Post, Camera, Transform, Vec2, Vec3, Orbit, TextureLoader, RenderTarget } from 'ogl'
import anime from 'animejs'

// import GLSinWaves from './GLSinWaves'
import GLWaves from './GLWaves'
import GLBoat from './GLBoat'
import GLBoatKraken from './GLBoatKraken'
import GLShrek from './GLShrek'

import fragPassSundisk from '@shaders/post/passSundisk.frag'
import fragPassRadialBlur from '@shaders/post/passRadialBlur.frag'
import fragPassBlur from '@shaders/post/passBlur.frag'
import fragPassComposition from '@shaders/post/passComposition.frag'

import emitter from '@utils/emitter'

export default class GLScene {
  constructor ({ canvas, isDesktop }) {
    this.canvas = canvas
    this.isDesktop = isDesktop
    this.isDebug = false

    // Scene vars set in handleResize
    this.shouldRenderPast = true
    this.lt = Math.random() * 10
    this.sceneVars = {
      isDesktop: this.isDesktop,
      width: 0,
      height: 0,
      vWidth: 0,
      vHeight: 0,
      aspectRatio: 0,
      cu: 0,
    }

    this.shouldShakeCamera = false
    this.currentCamera = null
    this.mouse = new Vec2(this.isDesktop ? -1 : 0.5)
    this.animSunIntensity = 0
    this.animSunPosY = this.isDesktop ? 0.55 : 0.525

    this.children = []
    this.images = []
    this.videos = []

    // RENDERING
    this.create()

    // EVENTS
    // Handle resize is fired once from above
    this.handleResize()
  }

  create () {
    // RENDERING
    this.createRenderer()
    this.createPost()
    this.createCamera()

    this.updateSceneVars()

    // COMPONENTS
    this.createScene()

    // EVENTS
    this.createEventListeners()
  }

  createRenderer () {
    const pixelRatio = Math.min(window.devicePixelRatio ? window.devicePixelRatio : 1, 1.5)
    this.renderer = new Renderer({ alpha: true, dpr: pixelRatio, canvas: this.canvas })

    this.gl = this.renderer.gl
    this.gl.clearColor(0, 0, 0, 0)
  }

  createPost () {
    this.post = new Post(this.gl)
    this.sundiskRenderTarget = new RenderTarget(this.gl)
    this.pastSundiskRenderTarget = new RenderTarget(this.gl)

    // TODO: MOBILE CHANGE DPR
    this.postSunrays = new Post(this.gl, { dpr: this.renderer.dpr, targetOnly: true })
    this.postResolution = { value: new Vec2() }
    this.bloomResolution = { value: new Vec2() }
    this.sundiskPosition = { value: new Vec2(0.5, this.animSunPosY) }
    this.sundiskRadius = this.isDesktop ? 0.2 : 0.3
    this.blueNoiseFrequency = { value: this.isDesktop ? 1 + window.innerWidth / 1920 : 1 }

    const radialIterations = 10
    const radialSize = 0.75
    const blurIterations = 3
    const blurSize = 3

    const texClouds = TextureLoader.load(this.gl, {
      src: './static/textures/noise-perlin-opti.png',
      wrapS: this.gl.REPEAT,
      wrapT: this.gl.REPEAT,
    })

    const texBlueNoise = TextureLoader.load(this.gl, {
      src: './static/textures/blue-noise-opti.png',
      wrapS: this.gl.REPEAT,
      wrapT: this.gl.REPEAT,
    })

    this.passSundisk = this.postSunrays.addPass({
      fragment: fragPassSundisk,
      uniforms: {
        uTime: { value: this.lt },
        uResolution: this.bloomResolution,
        tClouds: { value: texClouds },
        uThreshold: { value: 0.7 },
        uSundiskPos: this.sundiskPosition,
        uSundiskRadius: { value: this.sundiskRadius },
        uSundiskIntensity: { value: this.animSunIntensity },
      },
    })

    for (let i = 0; i < radialIterations; i++) {
      this.postSunrays.addPass({
        fragment: fragPassRadialBlur,
        uniforms: {
          uResolution: this.bloomResolution,
          uSundiskPos: this.sundiskPosition,
          uSundiskRadius: { value: this.sundiskRadius },
          uBlurSize: { value: radialSize - radialSize * (i / radialIterations) },
        },
      })
    }

    const passBlurHorizontal = this.postSunrays.addPass({
      fragment: fragPassBlur,
      uniforms: {
        uResolution: this.bloomResolution,
        uDirection: { value: new Vec2(blurSize, 0) },
      },
    })

    const passBlurVertical = this.postSunrays.addPass({
      fragment: fragPassBlur,
      uniforms: {
        uResolution: this.bloomResolution,
        uDirection: { value: new Vec2(0, blurSize) },
      },
    })

    for (let i = 0; i < blurIterations; i++) {
      this.postSunrays.passes.push(passBlurHorizontal, passBlurVertical)
    }

    this.passComposition = this.post.addPass({
      fragment: fragPassComposition,
      uniforms: {
        uTime: { value: this.lt },
        uResolution: this.postResolution,
        tBlueNoise: { value: texBlueNoise },
        tPerlin: { value: texClouds },
        tPastScene: { value: null },
        tSunrays: { value: this.sundiskRenderTarget.texture },
        tPastSunrays: { value: this.pastSundiskRenderTarget.texture },
        uBlueNoiseFrequency: this.blueNoiseFrequency,
        uSundiskPos: this.sundiskPosition,
        uSundiskRadius: { value: this.sundiskRadius },
        uSundiskIntensity: { value: this.animSunIntensity },
        uMouse: { value: this.mouse },
        uMaskRadius: { value: 0.4 },
        uMaskValue: { value: 0 },
      },
    })
  }

  createCamera () {
    this.camera = new Camera(this.gl, { fov: 50 })
    this.camera.position.set(0, 0.75, 4)
    this.camera.lookAt([0, 0, 0])

    this.debugCamera = new Camera(this.gl, { fov: 50 })
    this.debugCamera.position.set(0, 1, 4)

    this.orbit = new Orbit(this.debugCamera, {
      target: new Vec3(0, 0, 0),
      enabled: this.isDebug,
    })

    this.currentCamera = this.camera
  }

  createScene () {
    this.scene = new Transform()
    this.pastScene = new Transform()

    // COMPONENTS
    // PRESENT SCENE
    this.waves = new GLWaves({
      gl: this.gl,
      parent: this.scene,
      sceneVars: this.sceneVars,
    })

    this.boat = new GLBoat({
      gl: this.gl,
      parent: this.scene,
      sceneVars: this.sceneVars,
    })

    this.shrek = new GLShrek({
      gl: this.gl,
      parent: this.scene,
      sceneVars: this.sceneVars,
    })

    // PAST SCENE
    this.pastWaves = new GLWaves({
      gl: this.gl,
      parent: this.pastScene,
      sceneVars: this.sceneVars,
      paused: true,
    })

    this.pastBoat = new GLBoatKraken({
      gl: this.gl,
      parent: this.pastScene,
      sceneVars: this.sceneVars,
    })

    this.children = [...this.children, this.waves, this.boat, this.shrek, this.pastWaves, this.pastBoat]
  }

  createEventListeners () {
    emitter.on('KONAMI', this.handleKonami.bind(this))
  }

  updateSceneVars () {
    // RENDERING
    this.sceneVars.width = window.innerWidth
    this.sceneVars.height = window.innerHeight
    this.sceneVars.aspectRatio = window.innerWidth / window.innerHeight

    // VARIABLE DIMENS
    const vFov = this.camera.fov * (Math.PI / 180)
    const vHeight = 2 * Math.tan(vFov / 2) * this.camera.position.z
    const vWidth = vHeight * this.camera.aspect

    this.sceneVars.vHeight = vHeight
    this.sceneVars.vWidth = vWidth
  }

  // EVENTS
  handleAssetsLoad (assets) {
    // setTimeout(() => {
    //   const loadingLayer = document.querySelector('[js-loading]')
    //   loadingLayer.classList.add('is-hidden')
    // }, 1000)
  }

  handleResize () {
    this.updateSceneVars()

    this.camera.perspective({ aspect: this.sceneVars.aspectRatio })
    this.renderer.setSize(this.sceneVars.width, this.sceneVars.height)

    // POST
    this.postResolution.value.set(this.sceneVars.width, this.sceneVars.height)
    this.bloomResolution.value.set(this.postSunrays.options.width, this.postSunrays.options.height)
    this.blueNoiseFrequency.value = this.isDesktop ? Math.ceil(1 + this.sceneVars.width / 1920) : 1
    this.postSunrays.resize()
    this.post.resize()

    // COMPONENTS
  }

  handleMouseMove (e) {
    this.mouse.set(e.x / this.renderer.width, 1.0 - e.y / this.renderer.height)
  }

  handleMouseDown (e) {}

  handleMouseUp (e) {
    if (this.isDebug) return

    if (this.passComposition.uniforms.uMaskValue.value === 1) {
      setTimeout(() => {
        emitter.trigger('AUDIO_SYNTH_OFF')
        this.canvas.style.cursor = 'pointer'
      }, 500)

      anime({
        targets: this.passComposition.uniforms.uMaskValue,
        value: 0,
        duration: 1000,
        easing: 'easeInExpo',
      })
    } else if (this.passComposition.uniforms.uMaskValue.value === 0) {
      emitter.trigger('AUDIO_SYNTH_ON')

      setTimeout(() => {
        this.canvas.style.cursor = 'none'
      }, 500)

      anime({
        targets: this.passComposition.uniforms.uMaskValue,
        value: 1,
        duration: 2000,
        easing: 'easeInOutExpo',
      })
    }
  }

  handleKeyDown (e) {}

  handleKeyUp (e) {
    switch (e.keyCode) {
      case 68:
        this.isDebug = !this.isDebug

        if (this.isDebug) {
          this.currentCamera = this.debugCamera
          this.orbit.enabled = true
        } else {
          this.currentCamera = this.camera
          this.orbit.enabled = false
        }
        break
    }
  }

  handleKonami () {
    this.shouldShakeCamera = true
    this.shrek.moveUp(() => {
      this.shouldShakeCamera = false
    })
  }

  handleLoadingHide () {
    anime
      .timeline({
        complete: () => {
          this.shouldRenderPast = false
        },
      })
      .add(
        {
          targets: this,
          animSunIntensity: [0, 1],
          duration: 3000,
          easing: 'easeInCubic',
        },
        1000,
      )
      .add(
        {
          targets: this,
          animSunPosY: [0.4, this.isDesktop ? 0.55 : 0.525],
          duration: 6000,
          easing: 'easeOutCubic',
        },
        1000,
      )
  }

  update (t) {
    this.lt += 0.001

    // COMPONENTS
    this.children.forEach((child) => {
      if (child) child.update(t)
    })

    // DEBUG
    this.orbit.update()

    // CAMERA
    if (this.shouldShakeCamera) {
      this.camera.position.set(Math.random() * 0.02, 0.75 + Math.random() * 0.02, 4)
    }

    // RENDERING
    this.post.targetOnly = true
    this.passComposition.enabled = false

    // RENDER PAST
    if (this.shouldRenderPast) {
      this.post.render({
        scene: this.pastScene,
        camera: this.currentCamera,
      })
    }

    const pastSceneTexture = this.post.uniform.value

    if (this.shouldRenderPast) {
      this.passSundisk.uniforms.uTime.value = 7
      this.postSunrays.render({ texture: pastSceneTexture, targetOnly: true, target: this.pastSundiskRenderTarget })
    }

    // RENDER PRESENT
    this.post.render({
      scene: this.scene,
      camera: this.currentCamera,
    })

    this.passSundisk.uniforms.uTime.value = this.lt
    this.passSundisk.uniforms.uSundiskIntensity.value = this.animSunIntensity
    this.passSundisk.uniforms.uSundiskPos.value.y = this.animSunPosY
    this.postSunrays.render({ texture: this.post.uniform.value, targetOnly: true, target: this.sundiskRenderTarget })

    // RENDER WITH FINAL PROCESSING
    this.passComposition.uniforms.uTime.value = this.lt
    this.passComposition.uniforms.tPastScene.value = pastSceneTexture
    this.passComposition.uniforms.uSundiskIntensity.value = this.animSunIntensity
    this.passComposition.uniforms.uSundiskPos.value.y = this.animSunPosY

    this.post.targetOnly = false
    this.passComposition.enabled = true
    this.post.render({ texture: this.post.uniform.value })
  }
}
